import { TOAST_UPDATE_PERSONAL_INFO } from '@/constants/toast';
import { useMe } from '@/context/me/me.context';
import useAnalyticsCalls from '@/hooks/use-analytics';
import {
  useCancelSubscriptionMutation,
  useGetMembershipStatus,
  useRenewSubscriptionMutation,
} from '@/queries/membership.queries';
import { useDisclosure, useToast } from '@cmpsr/components';
import {
  useCreateCancellationRequestV1SubscriptionCancellationRequestsPost,
  useGetSubscriptionsV1SubscriptionsGet,
} from '@/api/generated-catch-backend/subscriptions/subscriptions';
import { CancellationReason } from '@/api/generated-catch-backend/model';
import { useQueryClient } from '@tanstack/react-query';

export default function useEditMembershipController() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: userData } = useMe();
  const membershipId = userData?.membership.id as string;

  const { data: membershipData } = useGetMembershipStatus(membershipId);
  const { data: subscriptionData } = useGetSubscriptionsV1SubscriptionsGet();

  const type = membershipData?.type as string;
  const cancelAtPeriodEnd = membershipData?.cancelAtPeriodEnd as boolean;
  const price = (membershipData?.price?.amount as number) ?? 0;
  const usdPrice = price / 100;
  const date = new Date(membershipData?.renewalDate as string);
  const analytics = useAnalyticsCalls();

  let renewalDate = '';

  if (membershipData?.renewalDate) {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    renewalDate = new Intl.DateTimeFormat('en-US', options).format(date);
  }

  const cancelSubscriptionMutation = useCancelSubscriptionMutation();
  const renewSubscriptionMutation = useRenewSubscriptionMutation();
  const createCancellationRequestMutation = useCreateCancellationRequestV1SubscriptionCancellationRequestsPost();
  const { isPending: isSubmitting } = createCancellationRequestMutation;

  const {
    isOpen: showCancelMembershipModal,
    onOpen: onOpenShowCancelMembershipModal,
    onClose: onHideCancelMembershipModal,
  } = useDisclosure();

  const {
    isOpen: showRenewMembershipModal,
    onOpen: onOpenShowRenewMembershipModal,
    onClose: onHideRenewMembershipModal,
  } = useDisclosure();

  const onShowCancelMembershipModal = () => {
    onOpenShowCancelMembershipModal();
    analytics.callMembershipCancel();
  };

  const onShowRenewMembershipModal = () => {
    onOpenShowRenewMembershipModal();
    analytics.callMembershipRenew();
  };

  function handleCancelMembership() {
    return cancelSubscriptionMutation
      .mutateAsync(membershipId)
      .then(() => {
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          description: 'Your membership was successfully cancelled',
          status: 'success',
        });
        onHideCancelMembershipModal();
      })
      .catch((error) => {
        console.error(error);
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          title: `Error ${error.response?.data?.statusCode ?? ''}`,
          description: error.response?.data?.message ?? error.message,
          status: 'error',
        });
      });
  }

  function handleRenewMembership() {
    return renewSubscriptionMutation
      .mutateAsync(membershipId)
      .then(() => {
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          description: 'Your membership was successfully renewed',
          status: 'success',
        });
        onHideRenewMembershipModal();
      })
      .catch((error) => {
        console.error(error);
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          title: `Error ${error.response?.data?.statusCode ?? ''}`,
          description: error.response?.data?.message ?? error.message,
          status: 'error',
        });
      });
  }

  function handleSubmitCancellationRequest(reason: CancellationReason, reasonDescription?: string) {
    const stripeSubscriptionId = subscriptionData?.id;
    if (!stripeSubscriptionId) {
      toast({
        ...TOAST_UPDATE_PERSONAL_INFO,
        title: 'Error',
        description: 'Unable to find subscription information',
        status: 'error',
      });
      return Promise.reject(new Error('No subscription ID found'));
    }

    return createCancellationRequestMutation
      .mutateAsync({
        data: {
          stripeSubscriptionId,
          reason,
          reasonDescription: reasonDescription || null,
        },
      })
      .then(() => {
        // Invalidate queries to refresh the subscription data
        queryClient.invalidateQueries({ queryKey: ['/v1/subscriptions'] });
        queryClient.invalidateQueries({ queryKey: ['membership-status', 'get-membership-status', membershipId] });
        queryClient.invalidateQueries({ queryKey: ['me', 'get'] });

        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          description: `Your subscription has been cancelled and will end on ${renewalDate}`,
          status: 'success',
        });
        onHideCancelMembershipModal();
      })
      .catch((error) => {
        console.error(error);
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          title: `Error ${error.response?.data?.statusCode ?? ''}`,
          description: error.response?.data?.message ?? error.message,
          status: 'error',
        });
      });
  }

  const isTrialUser = userData?.membership?.status === 'trialing';

  // Format subscription status for display (e.g., "active" -> "Active", "past_due" -> "Past Due", "trialing" -> "Trial")
  const formatStatus = (status?: string) => {
    if (!status) return undefined;
    if (status.toLowerCase() === 'trialing') return 'Trial';
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const subscriptionStatus = formatStatus(subscriptionData?.status);
  const hasPendingCancellation = subscriptionData?.hasPendingCancellation ?? false;

  return {
    type,
    cancelAtPeriodEnd,
    usdPrice,
    renewalDate,
    showCancelMembershipModal,
    onShowCancelMembershipModal,
    onHideCancelMembershipModal,
    handleCancelMembership,
    handleSubmitCancellationRequest,
    handleRenewMembership,
    showRenewMembershipModal,
    onShowRenewMembershipModal,
    onHideRenewMembershipModal,
    isTrialUser,
    subscriptionStatus,
    hasPendingCancellation,
    isSubmitting,
  };
}
